import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-ring/40 active:scale-[0.98]",
  {
    variants: {
      variant: {
        primary:
          "bg-brand-gradient text-brand-gradient-foreground shadow-elevated hover:brightness-110",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-accent hover:text-accent-foreground",
        outline:
          "border border-border bg-card hover:bg-accent hover:text-accent-foreground",
        ghost:
          "hover:bg-accent hover:text-accent-foreground",
        destructive:
          "bg-destructive text-destructive-foreground hover:brightness-110",
        success:
          "bg-success text-success-foreground hover:brightness-110",
        link:
          "text-primary underline-offset-4 hover:underline",
      },
      size: {
        sm: "h-8 px-3 text-xs",
        md: "h-10 px-4",
        lg: "h-11 px-6 text-base",
        xl: "h-12 px-8 text-base",
        icon: "size-10",
        // Compact square icon buttons for toolbars and message actions, where a
        // 40px target would crowd the row.
        "icon-sm": "size-8",
        "icon-xs": "size-6 [&_svg]:size-3.5",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

import { Loader } from "./loader";

const Spinner = () => <Loader size="sm" />;

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading, disabled, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    // HTML default for `<button>` inside `<form>` is `type="submit"`. When someone
    // forgets `type="button"` on a button in a form, the form submits by accident,
    // which the Next.js App Router reads as a server action and which can leave the
    // page "unclickable" (the pending POST blocks navigation). So the default here is
    // the safe one - a real submit button has to say `type="submit"` itself.
    const { type, ...rest } = props as { type?: "button" | "submit" | "reset" } & typeof props;
    const safeType = !asChild ? (type ?? "button") : type;
    return (
      <Comp
        ref={ref}
        data-slot="button"
        {...(safeType ? { type: safeType } : {})}
        disabled={disabled || loading}
        className={cn(buttonVariants({ variant, size }), className)}
        {...rest}
      >
        {loading ? <Spinner /> : children}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { buttonVariants };
